import { resolveCountedWeightPounds } from "@CommonUtils/pog/productWeight";
import {
  getDeadSpans,
  getEffectiveMerchandisableWidth,
  getLiveSpans,
  type ShelfWidthLike,
} from "@CommonUtils/pog/shelfMerchandising";
import { rackNameOf } from "@CommonUtils/pog/racks";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import type {
  PlanogramPegboard,
  PlanogramPegboardPlacement,
} from "@CommonTypes/merchflow/pog/pegboard";
import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import type { AttributeCandidate } from "../domain/studioTypes";
import { sizeFamilyOf } from "../domain/sizeFamily";
import {
  isPlaceholderValue,
  type StudioItem,
  type StudioPlacement,
  type StudioRunGeometry,
} from "../domain/studioTypes";

export const POG_ATTRIBUTE_CANDIDATES: readonly AttributeCandidate[] = [
  { attr: "subCategory", label: "Subcategory" },
  { attr: "brand", label: "Brand" },
  { attr: "cdt1", label: "CDT 1" },
  { attr: "cdt2", label: "CDT 2" },
  { attr: "cdt3", label: "CDT 3" },
  { attr: "variant", label: "Variant" },
  { attr: "size", label: "Size" },
];

export const POG_PREFERRED_ORDER = POG_ATTRIBUTE_CANDIDATES.map((candidate) => candidate.attr);

const POG_AUTHORED_ATTRS: ReadonlySet<string> = new Set(POG_PREFERRED_ORDER);

/** Every spelling of absence collapses to ONE bucket; left distinct they fabricate phantom level splits that hide real order violations. */
const DEFAULT_PLACEHOLDER_SPELLINGS: readonly string[] = [
  "na",
  "n/a",
  "n-a",
  "none",
  "unknown",
  "unspecified",
];

const UNSPECIFIED = "Unspecified";

const POG_BRAND_ATTR = "brand";

/** The words of a product name: "DA-NA  SHAMPOO 550ML" reads DA, NA, SHAMPOO, 550ML. */
const nameWordsOf = (name: string | null | undefined): Set<string> =>
  new Set(
    String(name ?? "")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean),
  );

/** For the brand only, a placeholder spelling the product's own name carries as a word is that
 *  product's brand, not a missing one: brand "NA" on "DA-NA SHAMPOO" is the brand DA-NA sells under.
 *  It is kept only where the Studio reads it as a value, so the two never disagree about it. */
const attrValue = (
  value: string | null | undefined,
  placeholderSpellings: readonly string[] | false,
  brandNameWords?: ReadonlySet<string>,
): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (placeholderSpellings === false) return trimmed;
  const placeholders = new Set(
    placeholderSpellings.map((spelling) => spelling.trim().toLowerCase()),
  );
  const lower = trimmed.toLowerCase();
  if (!placeholders.has(lower)) return trimmed;
  return brandNameWords?.has(lower) && !isPlaceholderValue(trimmed) ? trimmed : UNSPECIFIED;
};

export type PogQuantityUnit = "fl oz" | "l" | "ml" | "oz" | "lb" | "loads" | "ct";

const DEFAULT_NAME_UNIT_PREFERENCE: readonly PogQuantityUnit[] = [
  "fl oz",
  "l",
  "ml",
  "oz",
  "lb",
  "loads",
  "ct",
];

const DEFAULT_UNIT_ALIASES: Readonly<Record<string, PogQuantityUnit>> = {
  "fl oz": "fl oz",
  "fl. oz": "fl oz",
  "fluid ounce": "fl oz",
  "fluid ounces": "fl oz",
  oz: "oz",
  ounce: "oz",
  ounces: "oz",
  lb: "lb",
  lbs: "lb",
  pound: "lb",
  pounds: "lb",
  ml: "ml",
  l: "l",
  liter: "l",
  liters: "l",
  litre: "l",
  litres: "l",
  load: "loads",
  loads: "loads",
  ct: "ct",
  count: "ct",
  pac: "ct",
  pacs: "ct",
  pod: "ct",
  pods: "ct",
};

/** A rank of 0 or less means the pogger was given none. */
const prioritiesOf = (item: {
  cdt1Priority?: number;
  cdt2Priority?: number;
  cdt3Priority?: number;
}): Record<string, number> | undefined => {
  const priorities: Record<string, number> = {};
  const ranks: Record<string, number | undefined> = {
    cdt1: item.cdt1Priority,
    cdt2: item.cdt2Priority,
    cdt3: item.cdt3Priority,
  };
  for (const [attr, rank] of Object.entries(ranks)) {
    if (typeof rank === "number" && rank > 0) priorities[attr] = rank;
  }
  return Object.keys(priorities).length ? priorities : undefined;
};

/** Stated weights only — a pack-name guess does not qualify. */
const statedWeightKg = (item: {
  name?: string;
  size?: string;
  unitWeight?: number | null;
  trayWeight?: number | null;
  caseWeight?: number | null;
  weightUom?: string | null;
  noOfUnitsInTray?: number | null;
  noOfUnitsInCase?: number | null;
}) => {
  const resolved = resolveCountedWeightPounds(
    { ...item, name: item.name ?? "", size: item.size ?? "" },
    "UNIT",
  );
  return resolved === null || resolved.provenance !== "measured"
    ? undefined
    : +(resolved.pounds * KG_PER_POUND).toFixed(3);
};

const KG_PER_POUND = 0.45359237;

const regexEscape = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const quantityTokensOf = (
  name: string,
  aliases: Readonly<Record<string, PogQuantityUnit>>,
): { magnitude: number; unit: PogQuantityUnit }[] => {
  const normalizedAliases = Object.fromEntries(
    Object.entries(aliases).map(([source, unit]) => [
      source.toLowerCase().replace(/\s+/g, " "),
      unit,
    ]),
  );
  const spellings = Object.keys(normalizedAliases)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (spellings.length === 0) return [];
  const pattern = new RegExp(
    `(\\d+(?:\\.\\d+)?)\\s*(${spellings.map(regexEscape).join("|")})(?![a-z])`,
    "gi",
  );
  return [...name.matchAll(pattern)].flatMap((match) => {
    const unit = normalizedAliases[match[2].toLowerCase().replace(/\s+/g, " ")];
    return unit ? [{ magnitude: parseFloat(match[1]), unit }] : [];
  });
};

/** Liquid ounces are fluid; a powder "55 oz" stays mass. */
const DEFAULT_LIQUID_NAME_TERMS: readonly string[] = ["liquid", "fl oz", "fluid ounce"];

const searchableTerm = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, "");

const nameMatchesAnyTerm = (name: string, terms: readonly string[] | false): boolean => {
  if (terms === false) return false;
  const searchableName = searchableTerm(name);
  return terms.some((term) => {
    const searchable = searchableTerm(term);
    return searchable.length > 0 && searchableName.includes(searchable);
  });
};

const enrichQuantityUnit = (
  quantity: string | null,
  name: string | null | undefined,
  liquidNameTerms: readonly string[] | false,
  unitPreference: readonly PogQuantityUnit[] | false,
  unitAliases: Readonly<Record<string, PogQuantityUnit>>,
  zeroIsMissing: boolean,
  liquidOuncesAreFluid: boolean,
): string | null => {
  const text = String(name ?? "");
  const liquid = nameMatchesAnyTerm(text, liquidNameTerms);
  const tokens: { magnitude: number; unit: PogQuantityUnit }[] = [];
  for (const token of quantityTokensOf(text, unitAliases)) {
    tokens.push({
      magnitude: token.magnitude,
      unit: token.unit === "oz" && liquid && liquidOuncesAreFluid ? "fl oz" : token.unit,
    });
  }
  const columnMagnitude = quantity
    ? parseFloat(quantity.match(/-?\d+(?:\.\d+)?/)?.[0] ?? "0") || 0
    : 0;
  if ((!quantity || (zeroIsMissing && columnMagnitude === 0)) && unitPreference !== false) {
    for (const preferred of unitPreference || []) {
      const token = tokens.find((entry) => entry.unit === preferred && entry.magnitude > 0);
      if (token) return `${token.magnitude} ${token.unit}`;
    }
    return quantity;
  }
  if (!/^\d+(\.\d+)?$/.test(quantity!)) return quantity;
  // Never append "loads" — a liquid's load count often equals its fl-oz number.
  for (const preferred of unitPreference || []) {
    if (preferred === "loads") continue;
    const token = tokens.find(
      (entry) => entry.unit === preferred && Math.abs(entry.magnitude - columnMagnitude) < 0.05,
    );
    if (token) return `${quantity} ${token.unit}`;
  }
  return quantity;
};

const SIZE_TOKEN = /-?\d+(?:\.\d+)?/;

const reconcileQuantityLabels = (
  list: StudioItem[],
  quantityAttribute: string,
  blockAttributes: readonly string[],
  minimumFamilyEvidence: number,
  reconciliationRange: { minimum: number; maximum: number },
  liquidOuncesAreFluid: boolean,
): void => {
  const byBlock = new Map<string, StudioItem[]>();
  for (const item of list) {
    const key = blockAttributes.map((attr) => item.attrs[attr] ?? "").join("␟");
    const members = byBlock.get(key) ?? [];
    members.push(item);
    byBlock.set(key, members);
  }
  byBlock.forEach((members) => {
    const parsed = members
      .filter((item) => item.attrs[quantityAttribute])
      .map((item) => ({ item, ...sizeFamilyOf(String(item.attrs[quantityAttribute])) }));
    const volume = parsed.filter((entry) => entry.family === "volume");
    if (volume.length < minimumFamilyEvidence) return;
    const magnitudes = volume.map((entry) => entry.magnitude);
    // Tight floor, loose ceiling — a pod count next to fl-oz is below the volume range.
    const low = Math.min(...magnitudes) * reconciliationRange.minimum;
    const high = Math.max(...magnitudes) * reconciliationRange.maximum;
    for (const entry of parsed) {
      if (entry.family !== "bare" && !(liquidOuncesAreFluid && entry.family === "oz")) continue;
      if (entry.magnitude < low || entry.magnitude > high) continue;
      const number = String(entry.item.attrs[quantityAttribute]).match(SIZE_TOKEN)?.[0];
      if (!number) continue;
      entry.item.attrs = { ...entry.item.attrs, [quantityAttribute]: `${number} fl oz` };
    }
  });
};

/** One accepted band as the POG carries it: the shelf's OWN entry, not `sourceRule.shelfRule`.
 *  A served plan's rule text can name shelves the plan no longer has (a 3-shelf store pog
 *  carrying rules written for shelves 4 to 6), so the shelf a rule applies to is the shelf the
 *  entry was found on. `value` is a promoted band label and need not match any single column. */
export interface PogShelfRestriction {
  run: number;
  /** Physical shelf row rank, 1 = top — the same rank a placement carries. */
  shelf: number;
  /** 1-based bay the entry was found on. */
  bay: number;
  type: "MAY_HAVE" | "MUST_HAVE" | "MUST_NOT_HAVE";
  value: string;
  /** 1-based board of its bay the entry was found on, and how many boards share its cell (its
   *  row in its bay): a rack's boards share their host's. Absent reads as the cell's one board. */
  board?: number;
  boards?: number;
}

export interface StudioPogData {
  items: StudioItem[];
  runs: StudioRunGeometry[];
  candidates: AttributeCandidate[];
  /** Every band entry the POG's shelves carry, keyed to physical rows. */
  shelfRestrictions: PogShelfRestriction[];
}

type WallBoard = ShelfWidthLike & {
  getYBottom: () => number;
  thickness: number;
  type?: string;
  items?: readonly unknown[];
  uniqueId?: string;
  assembly?: string | null;
  standsOn?: string | null;
};

/** A riser, a non-merchandisable board or one obstructed end to end offers no shelf at all. */
const holdsNoProduct = (shelf: WallBoard) =>
  shelf.type === "RISER" || getEffectiveMerchandisableWidth(shelf) === 0;

/** The rank of a board that is no wall row: placement ranks are 1-based, so it matches none. */
const NO_ROW = 0;

/** Hole rows as the canvas counts them: one every vertical gap, both offsets excluded. */
const pegboardRowCountOf = (pegboard: PlanogramPegboard): number => {
  const { height, holesOffsetVertical: offset, holesGapVertical: gap } = pegboard;
  return [height, offset, gap].every(Number.isFinite) && gap > 0
    ? Math.abs(Math.floor((height - 2 * offset) / gap))
    : 0;
};

const pegboardRowYOf = (pegboard: PlanogramPegboard, holeNoTop: number): number =>
  pegboard.yBottom +
  pegboard.height -
  pegboard.holesOffsetVertical -
  (holeNoTop - 1) * pegboard.holesGapVertical;

/** Run-absolute x of each hole the placement hangs from, like the shelf items' merged x. */
const holeXsOf = (placement: PlanogramPegboardPlacement): number[] => {
  const pegboard = placement.pegboard;
  const xLeft = (pegboard.sectionX ?? pegboard.bay.getXLeft()) + pegboard.holesOffsetHorizontal;
  return placement.holes.map((hole) => xLeft + (hole.holeNoLeft - 1) * pegboard.holesGapHorizontal);
};

const isValidHole = (hole: { holeNoLeft?: unknown; holeNoTop?: unknown } | null): boolean =>
  [hole?.holeNoLeft, hole?.holeNoTop].every(
    (number) => Number.isInteger(number) && (number as number) >= 1,
  );

/** Every hole, not just the anchor: any bad hole leaves the placement's row or x unknowable. */
const isSupportedPegboardPlacement = (placement: PlanogramPegboardPlacement): boolean =>
  placement.holes.length > 0 && placement.holes.every(isValidHole);

/** Placements the Studio cannot read; the Studio is unavailable while any exist. */
export const unsupportedPegboardPlacementsOf = (pogs: readonly Pog[]): string[] =>
  pogs.flatMap((pog) =>
    pog.planogram.bays.flatMap((bay) =>
      bay.pegboards.flatMap((pegboard) =>
        pegboard.placements
          .filter((placement) => !isSupportedPegboardPlacement(placement))
          .map((placement) => placement.uniqueId),
      ),
    ),
  );

/** The canvas ids of every shelf item and pegboard placement holding one of these SKUs. */
export const canvasIdsOfSkus = (pog: Pog, skuIds: ReadonlySet<string>): string[] =>
  pog.planogram.bays.flatMap((bay) => [
    ...bay.shelves.flatMap((shelf) =>
      shelf.items.flatMap((item) =>
        skuIds.has(item.productCode || item.id) ? [item.uniqueId] : [],
      ),
    ),
    ...bay.pegboards.flatMap((pegboard) =>
      pegboard.placements.flatMap((placement) =>
        skuIds.has(placement.pegProduct.productCode || placement.pegProduct.id)
          ? [placement.uniqueId]
          : [],
      ),
    ),
  ]);

const productsOfBay = (bay: PlanogramBay): BaseProduct[] => [
  ...bay.shelves.flatMap((shelf) =>
    shelf.items.filter((item): item is PlanogramItem => item instanceof PlanogramItem),
  ),
  ...bay.pegboards.flatMap((pegboard) =>
    pegboard.placements.map((placement) => placement.pegProduct),
  ),
];

interface HangLine {
  /** The bay the pegboard belongs to; its products count in the bay their holes are in. */
  bayIndex: number;
  /** 0-based bays the line physically crosses: the board at its section x, and every hole. */
  bays: ReadonlySet<number>;
  /** Raw POG units, like a board's y. */
  y: number;
  /** Left to right. */
  placements: readonly PlanogramPegboardPlacement[];
}

/** 0-based index of the bay standing at run x `x`, or -1: a pallet stands under its gondola bay. */
const bayIndexAt = (bays: readonly PlanogramBay[], x: number): number =>
  bays.findIndex((bay) => !bay.getBayAbove() && x >= bay.getXLeft() && x < bay.getXRight());

/** Every bay a pegboard crosses, read off the same x its products are placed at: the board
 *  from its section x for its bay's width, and every hole a product hangs from. */
const pegboardBaysOf = (
  pegboard: PlanogramPegboard,
  supported: readonly PlanogramPegboardPlacement[],
  ownBayIndex: number,
): number[] => {
  const planogramBays = pegboard.bay.planogram.bays;
  const xLeft = pegboard.sectionX ?? pegboard.bay.getXLeft();
  const xRight = xLeft + pegboard.bay.width;
  const crossed = planogramBays.flatMap((bay, index) =>
    !bay.getBayAbove() && bay.getXLeft() < xRight && bay.getXRight() > xLeft ? [index] : [],
  );
  for (const placement of supported) {
    for (const x of holeXsOf(placement)) {
      const index = bayIndexAt(planogramBays, x);
      if (index >= 0 && !crossed.includes(index)) crossed.push(index);
    }
  }
  return crossed.length > 0 ? crossed : [ownBayIndex];
};

/** Every hole row of each bay's pegboards, empty ones too as snaking counts them. */
const hangLinesOf = (bays: WallBays): HangLine[] =>
  bays.flatMap((bay, bayIndex) => {
    const byY = new Map<number, PlanogramPegboardPlacement[]>();
    const baysByY = new Map<number, Set<number>>();
    for (const pegboard of bay.pegboards ?? []) {
      const supported = pegboard.placements.filter(isSupportedPegboardPlacement);
      const crossed = pegboardBaysOf(pegboard, supported, bayIndex);
      const holeRows = new Set(
        Array.from({ length: pegboardRowCountOf(pegboard) }, (_, index) => index + 1),
      );
      for (const placement of supported) holeRows.add(placement.holes[0].holeNoTop);
      for (const holeNoTop of holeRows) {
        const y = pegboardRowYOf(pegboard, holeNoTop);
        if (!byY.has(y)) byY.set(y, []);
        const lineBays = baysByY.get(y) ?? new Set<number>();
        crossed.forEach((index) => lineBays.add(index));
        baysByY.set(y, lineBays);
      }
      for (const placement of supported) {
        byY.get(pegboardRowYOf(pegboard, placement.holes[0].holeNoTop))?.push(placement);
      }
    }
    return [...byY].map(([y, placements]) => ({
      bayIndex,
      bays: baysByY.get(y) ?? new Set([bayIndex]),
      y,
      placements: placements
        .map((placement) => ({ placement, x: Math.min(...holeXsOf(placement)) }))
        .sort((a, b) => a.x - b.x || a.placement.uniqueId.localeCompare(b.placement.uniqueId))
        .map((entry) => entry.placement),
    }));
  });

type WallBays = readonly {
  width: number;
  shelves: readonly WallBoard[];
  pegboards?: readonly PlanogramPegboard[];
}[];

/** The wall's rows: its boards' rows, and every pegboard hole row slotted between them by height.
 *  A hang line is a row of its own; one at the same height on several bays is one row. */
const shelfRowsOf = (
  bays: WallBays,
): {
  rankOf: (bayIndex: number, shelfIndex: number) => number;
  rackOf: (bayIndex: number, shelfIndex: number) => RackSlot | undefined;
  rows: ShelfRow[];
  hangLines: readonly HangLine[];
  hangRankOf: (y: number) => number;
} => {
  const boards = boardRowsOf(bays);
  const hangLines = hangLinesOf(bays);
  if (hangLines.length === 0) return { ...boards, hangLines, hangRankOf: () => 0 };

  const keyOf = (y: number) => y.toFixed(3);
  const lines = new Map<string, { y: number; bays: Set<number> }>();
  for (const line of hangLines) {
    const entry = lines.get(keyOf(line.y)) ?? { y: line.y, bays: new Set<number>() };
    line.bays.forEach((index) => entry.bays.add(index));
    lines.set(keyOf(line.y), entry);
  }
  const bayStarts: number[] = [];
  let cursor = 0;
  for (const bay of bays) {
    bayStarts.push(cursor);
    cursor += bay.width;
  }
  const spansOf = (bayIndices: ReadonlySet<number>): ShelfRow["spans"] => {
    const spans: { xCm: number; widthCm: number; bays: number[] }[] = [];
    for (const bayIndex of [...bayIndices].sort((a, b) => a - b)) {
      const last = spans[spans.length - 1];
      if (last && last.bays[last.bays.length - 1] === bayIndex) {
        last.widthCm += bays[bayIndex].width;
        last.bays.push(bayIndex + 1);
      } else {
        spans.push({
          xCm: bayStarts[bayIndex],
          widthCm: bays[bayIndex].width,
          bays: [bayIndex + 1],
        });
      }
    }
    return spans;
  };

  type Entry = { y: number; board: ShelfRow } | { y: number; key: string; bays: Set<number> };
  const entries: Entry[] = [
    ...boards.rows.map((board) => ({ y: board.y, board })),
    ...[...lines].map(([key, line]) => ({ y: line.y, key, bays: line.bays })),
  ].sort((a, b) => b.y - a.y || Number("key" in a) - Number("key" in b));
  const rankOfBoardRow = new Map<number, number>();
  const rankOfLine = new Map<string, number>();
  const rows = entries.map((entry, index): ShelfRow => {
    const rank = index + 1;
    if ("board" in entry) {
      rankOfBoardRow.set(entry.board.rank, rank);
      return { ...entry.board, rank };
    }
    rankOfLine.set(entry.key, rank);
    return {
      rank,
      y: entry.y,
      thicknessCm: 0,
      spans: spansOf(entry.bays),
      deadSpans: [],
      pegboard: true,
    };
  });
  return {
    rankOf: (bayIndex, shelfIndex) => {
      const boardRank = boards.rankOf(bayIndex, shelfIndex);
      return rankOfBoardRow.get(boardRank) ?? boardRank;
    },
    rackOf: boards.rackOf,
    rows,
    hangLines,
    hangRankOf: (y) => rankOfLine.get(keyOf(y)) ?? 0,
  };
};

/** Rows a wall really has. The bays of one wall need not carry the same shelves: on a ragged
 *  wall bay 5 can hold four boards where bay 4 holds three, so "the second shelf of bay 5" and
 *  "the second shelf of bay 4" are different heights. Keying a lane by the index within its bay
 *  therefore puts items from two different heights in one lane, and the plan then reads as
 *  though products overlapped. Rows are read off the boards' own heights instead.
 *
 *  On a wall whose bays all carry the same boards this returns exactly the bay indices, so a
 *  uniform plan measures the same as it always did.
 *
 *  The wall has as many rows as its fullest bay has shelves, at the median height of each
 *  shelf of its fullest bays (`wallRowHeightsOf`). Bays notched a few centimetres off their
 *  neighbours still read as the same rows: every bay's shelves go to the nearest rows, top to
 *  bottom, never two shelves of one bay in one row (`nearestRowsInOrder`). A rack board that
 *  lines up with a shelf takes that shelf's row; a rack's own tiers join the row below them. */
const boardRowsOf = (
  bays: WallBays,
): {
  rankOf: (bayIndex: number, shelfIndex: number) => number;
  rackOf: (bayIndex: number, shelfIndex: number) => RackSlot | undefined;
  rows: ShelfRow[];
} => {
  const boards: { bayIndex: number; shelfIndex: number; y: number; thickness: number }[] = [];
  bays.forEach((bay, bayIndex) =>
    bay.shelves.forEach((shelf, shelfIndex) => {
      // An empty board that holds no product is fixture, not shelf: it is no row of the wall.
      if (holdsNoProduct(shelf) && !shelf.items?.length) return;
      boards.push({
        bayIndex,
        shelfIndex,
        y: shelf.getYBottom(),
        thickness: shelf.thickness,
      });
    }),
  );
  if (boards.length === 0) return { rankOf: () => 1, rackOf: () => undefined, rows: [] };

  const shelfOf = (board: { bayIndex: number; shelfIndex: number }) =>
    bays[board.bayIndex].shelves[board.shelfIndex];
  const isRackBoard = (shelf: ReturnType<typeof shelfOf>) =>
    rackNameOf(shelf.assembly) !== null || Boolean(shelf.standsOn);
  const keyOf = (board: { bayIndex: number; shelfIndex: number }) =>
    `${board.bayIndex}|${board.shelfIndex}`;
  const thickest = boards.reduce((max, board) => Math.max(max, board.thickness), 0);

  /* DEV-3531 DEV-3717: rack boards (a rack's tiers) are their bay's internal shelving, not wall
     rows, so the rows come from the ordinary shelves. An obstructed shelf is a row. A wall of
     rack boards alone reads its rows off them. */
  const ordinary = boards.filter((board) => !isRackBoard(shelfOf(board)));
  const shelving = ordinary.length ? ordinary : boards;
  const levelsByBay = new Map<number, number[]>();
  for (const board of shelving) {
    levelsByBay.set(board.bayIndex, [...(levelsByBay.get(board.bayIndex) ?? []), board.y]);
  }
  /* A bay's levels: its boards' heights, top first; boards side by side within the thickest
     board of each other are one level. */
  for (const [bayIndex, ys] of levelsByBay) {
    levelsByBay.set(bayIndex, bayLevelsOf(ys, thickest));
  }
  const rowYs = wallRowHeightsOf([...levelsByBay.values()]);

  const rowIndexByBoard = new Map<string, number>();
  for (const [bayIndex, levels] of levelsByBay) {
    const rowOfLevel = nearestRowsInOrder(levels, rowYs);
    for (const board of shelving) {
      if (board.bayIndex !== bayIndex) continue;
      // Every board is within the thickest board of the level `bayLevelsOf` put it in.
      const level = levels.findIndex((y) => Math.abs(y - board.y) <= thickest);
      rowIndexByBoard.set(keyOf(board), rowOfLevel[level]);
    }
  }
  /* A rack board at the height of a shelf of its own bay (within the thickest board) is part of
     that shelf's row; any other rack board is a rack tier, and joins the row it stands over. */
  const local = new Set<string>();
  for (const board of boards) {
    if (rowIndexByBoard.has(keyOf(board))) continue;
    const level = ordinary
      .filter(
        (other) => other.bayIndex === board.bayIndex && Math.abs(other.y - board.y) <= thickest,
      )
      .sort((a, b) => Math.abs(a.y - board.y) - Math.abs(b.y - board.y))[0];
    if (level) {
      rowIndexByBoard.set(keyOf(board), rowIndexByBoard.get(keyOf(level))!);
      continue;
    }
    local.add(keyOf(board));
    const below = rowYs.findIndex((rowY) => rowY <= board.y);
    rowIndexByBoard.set(keyOf(board), below >= 0 ? below : rowYs.length - 1);
  }

  const rankByBoard = new Map<string, number>();
  for (const board of boards) rankByBoard.set(keyOf(board), rowIndexByBoard.get(keyOf(board))! + 1);
  // DEV-3717: a rack board's items are graded on the row of the host it stands on.
  const onHost = new Set<string>();
  for (const board of boards) {
    const standsOn = shelfOf(board).standsOn;
    if (!standsOn) continue;
    const hostIndex = bays[board.bayIndex].shelves.findIndex(
      (shelf) => shelf.uniqueId === standsOn && !isRackBoard(shelf),
    );
    const hostRank = rankByBoard.get(`${board.bayIndex}|${hostIndex}`);
    if (hostRank === undefined) continue;
    rankByBoard.set(keyOf(board), hostRank);
    onHost.add(keyOf(board));
  }
  // A board on its host is the rack's own tier even when a neighbour bay's shelf shares its height.
  const isLocal = (board: { bayIndex: number; shelfIndex: number; y: number }) =>
    onHost.has(keyOf(board)) || local.has(keyOf(board));

  const bayStarts: number[] = [];
  let cursor = 0;
  for (const bay of bays) {
    bayStarts.push(cursor);
    cursor += bay.width;
  }

  /* A rack reads as one board of its host's row, each tier a slice of its span, top down as
     the wall's rows are ranked, so the walk meets its tiers in the order of the rows above. */
  const rackByBoard = new Map<string, RackSlot>();
  const rackKeys = new Set(
    boards
      .filter(isLocal)
      .map((board) => `${board.bayIndex}|${rankByBoard.get(keyOf(board))}`),
  );
  for (const rackKey of rackKeys) {
    const levels = boards
      .filter((board) => `${board.bayIndex}|${rankByBoard.get(keyOf(board))}` === rackKey)
      .sort((a, b) => b.y - a.y);
    const bayIndex = levels[0].bayIndex;
    const bayWidth = bays[bayIndex].width;
    let from = Number.POSITIVE_INFINITY;
    let to = Number.NEGATIVE_INFINITY;
    for (const board of levels) {
      if (!isLocal(board)) continue;
      let at = 0;
      for (const dead of [...getDeadSpans(bays[bayIndex].shelves[board.shelfIndex]), null]) {
        const end = dead ? Math.min(dead.x, bayWidth) : bayWidth;
        if (end > at) {
          from = Math.min(from, at);
          to = Math.max(to, end);
        }
        if (dead) at = Math.max(at, dead.x + dead.width);
      }
    }
    if (!(to > from)) continue;
    const span = { from: bayStarts[bayIndex] + from, to: bayStarts[bayIndex] + to };
    // An empty level takes no slice: an empty tier is not a rack.
    const stocked = levels.filter((board) =>
      (bays[bayIndex].shelves[board.shelfIndex].items ?? []).some((item) => {
        const at = (item as Partial<PlanogramItem>).getCoordinates?.();
        return at !== undefined && inSpan(span, at);
      }),
    );
    if (!stocked.some(isLocal)) continue;
    stocked.forEach((board, level) =>
      rackByBoard.set(keyOf(board), {
        level,
        levels: stocked.length,
        ...span,
        rowY: local.has(keyOf(board)) ? board.y : rowYs[rowIndexByBoard.get(keyOf(board))!],
      }),
    );
  }

  const rows: ShelfRow[] = rowYs.map((y, index) => {
    const rank = index + 1;
    const members = boards
      .filter((board) => rankByBoard.get(`${board.bayIndex}|${board.shelfIndex}`) === rank)
      .sort((a, b) => a.bayIndex - b.bayIndex);
    /* Spans are the boards; a pole's strip stays inside its board's span, so facings either
       side of it stand on one shelf. The strips are carried apart, as neither air nor a hole. */
    const boardsAlong: { from: number; to: number; bayIndex: number }[] = [];
    const dead: { from: number; to: number }[] = [];
    const rackLive: { from: number; to: number }[] = [];
    const ordinaryIn = new Set(
      members
        .filter((member) => !isRackBoard(shelfOf(member)) && !holdsNoProduct(shelfOf(member)))
        .map((member) => member.bayIndex),
    );
    const along = (start: number, bayWidth: number, span: { x: number; width: number }) => ({
      from: start + Math.max(0, span.x),
      to: start + Math.min(bayWidth, span.x + span.width),
    });
    for (const member of members) {
      const bayWidth = bays[member.bayIndex].width;
      const start = bayStarts[member.bayIndex];
      const shelf = bays[member.bayIndex].shelves[member.shelfIndex];
      if (holdsNoProduct(shelf)) continue;
      boardsAlong.push({ from: start, to: start + bayWidth, bayIndex: member.bayIndex });
      if (isRackBoard(shelf) && rackByBoard.has(keyOf(member))) {
        rackLive.push(...getLiveSpans(shelf).map((span) => along(start, bayWidth, span)));
      }
      // DEV-3717: a rack board's dead part is where the rack ends only on an ordinary board's row.
      if (isRackBoard(shelf) && ordinaryIn.has(member.bayIndex)) continue;
      for (const strip of getDeadSpans(shelf)) {
        const part = along(start, bayWidth, strip);
        if (part.to > part.from) dead.push(part);
      }
    }
    // A stocked rack is shelf in its host's row, not the dead span the host leaves under it.
    const deadAlong = rackLive
      .reduce(
        (kept, live) =>
          kept.flatMap((strip) =>
            [
              { from: strip.from, to: Math.min(strip.to, live.from) },
              { from: Math.max(strip.from, live.to), to: strip.to },
            ].filter((part) => part.to > part.from),
          ),
        dead,
      )
      .sort((a, b) => a.from - b.from)
      .reduce<{ from: number; to: number }[]>((out, strip) => {
        const last = out[out.length - 1];
        if (last && strip.from <= last.to) last.to = Math.max(last.to, strip.to);
        else out.push({ ...strip });
        return out;
      }, []);
    boardsAlong.sort((a, b) => a.from - b.from);
    const spans: { from: number; to: number; bays: number[] }[] = [];
    for (const board of boardsAlong) {
      const last = spans[spans.length - 1];
      if (last && board.from <= last.to + 1e-6) {
        last.to = Math.max(last.to, board.to);
        if (!last.bays.includes(board.bayIndex)) last.bays.push(board.bayIndex);
        continue;
      }
      spans.push({ from: board.from, to: board.to, bays: [board.bayIndex] });
    }
    /* The row's height is the median of its shelves (`wallRowHeightsOf`), but a notched bay's
       board stands where it stands: each bay keeps its own, so the clear height under the
       board above is the bay's real one. A bay's shelf is its ordinary boards (a rack's tiers
       stand above them), the highest and thickest of them where two share the level. */
    const thicknessCm = members[0]?.thickness ?? 0;
    const bayBoards = [...new Set(members.map((member) => member.bayIndex))].flatMap((bayIndex) => {
      const inBay = members.filter(
        (member) => member.bayIndex === bayIndex && !holdsNoProduct(shelfOf(member)),
      );
      const own = inBay.filter((member) => !isRackBoard(shelfOf(member)));
      const shelf = own.length ? own : inBay.filter((member) => !isLocal(member));
      if (shelf.length === 0) return [];
      return [
        {
          bay: bayIndex + 1,
          y: Math.max(...shelf.map((member) => member.y)),
          thickness: Math.max(...shelf.map((member) => member.thickness)),
        },
      ];
    });
    const offRow = bayBoards.some((board) => board.y !== y || board.thickness !== thicknessCm);
    return {
      rank,
      y,
      thicknessCm,
      ...(offRow ? { bayBoards } : {}),
      spans: spans.map((span) => ({
        xCm: span.from,
        widthCm: span.to - span.from,
        bays: span.bays.map((bayIndex) => bayIndex + 1),
      })),
      deadSpans: deadAlong.map((strip) => ({ xCm: strip.from, widthCm: strip.to - strip.from })),
    };
  });

  return {
    rankOf: (bayIndex, shelfIndex) => rankByBoard.get(`${bayIndex}|${shelfIndex}`) ?? NO_ROW,
    rackOf: (bayIndex, shelfIndex) => rackByBoard.get(`${bayIndex}|${shelfIndex}`),
    rows,
  };
};

/** The wall row a board belongs to: the number every `StudioPlacement.shelf` carries. A writer
 *  turning a placement back into a board must look the row up here, never read it as the
 *  board's index within its bay — on a ragged wall the two differ. */
export const wallRowRankOf = (
  bays: Parameters<typeof shelfRowsOf>[0],
): ((bayIndex: number, shelfIndex: number) => number) => shelfRowsOf(bays).rankOf;

/** The slice of a rack a board takes in its row (DEV-3531), for a writer to find the board
 *  a placement came from; undefined for a board that is not part of a rack. */
export const wallRackSlotOf = (
  bays: Parameters<typeof shelfRowsOf>[0],
): ((bayIndex: number, shelfIndex: number) => RackSlot | undefined) => shelfRowsOf(bays).rackOf;

/** How many boards share a board's cell (its row in its bay): a rack's boards share their host's. */
const boardsInCellOf = (
  bays: WallBays,
  rankOf: (bayIndex: number, shelfIndex: number) => number,
): ((bayIndex: number, shelfIndex: number) => number) => {
  const counts = new Map<string, number>();
  const keyOf = (bayIndex: number, shelfIndex: number) =>
    `${rankOf(bayIndex, shelfIndex)}|${bayIndex}`;
  bays.forEach((bay, bayIndex) =>
    bay.shelves.forEach((_shelf, shelfIndex) => {
      counts.set(keyOf(bayIndex, shelfIndex), (counts.get(keyOf(bayIndex, shelfIndex)) ?? 0) + 1);
    }),
  );
  return (bayIndex, shelfIndex) => counts.get(keyOf(bayIndex, shelfIndex)) ?? 0;
};

const inSpan = (span: { from: number; to: number }, at: { xLeft: number; xRight: number }) => {
  const centre = (at.xLeft + at.xRight) / 2;
  return centre >= span.from && centre <= span.to;
};

/** A board's slice of a rack (DEV-3531), in the pog's own units along the wall. */
export interface RackSlot {
  level: number;
  levels: number;
  from: number;
  to: number;
  /** The height of the board's own row cluster: a wall row's `y` where the board lines up with it. */
  rowY: number;
}

interface ShelfRow {
  rank: number;
  y: number;
  thicknessCm: number;
  spans: readonly { xCm: number; widthCm: number; bays: readonly number[] }[];
  deadSpans: readonly { xCm: number; widthCm: number }[];
  /** Each bay's own board of the row where a bay's board stands off the row's `y` or thickness
   *  (a notched bay): its real height, for the clear height above it. */
  bayBoards?: readonly { bay: number; y: number; thickness: number }[];
  pegboard?: true;
}

/** One bay's shelf levels, top first: boards within `sameLevel` of the level's top board (two
 *  boards side by side at one notch) are one level. */
export const bayLevelsOf = (ys: readonly number[], sameLevel: number): number[] => {
  const levels: number[] = [];
  for (const y of [...ys].sort((a, b) => b - a)) {
    const open = levels[levels.length - 1];
    if (open === undefined || open - y > sameLevel) levels.push(y);
  }
  return levels;
};

/** The wall's row heights, top first: as many rows as the fullest bay has levels, each at the
 *  median height of that level across the fullest bays. */
export const wallRowHeightsOf = (levelsByBay: readonly (readonly number[])[]): number[] => {
  const most = Math.max(0, ...levelsByBay.map((levels) => levels.length));
  const fullest = levelsByBay.filter((levels) => levels.length === most);
  return Array.from({ length: most }, (_unused, index) => {
    const ys = fullest.map((levels) => levels[index]).sort((a, b) => a - b);
    const mid = ys.length >> 1;
    return ys.length % 2 ? ys[mid] : (ys[mid - 1] + ys[mid]) / 2;
  });
};

/** Each of a bay's levels (top first) to a row index of `rowYs` (top first): the nearest rows,
 *  top to bottom, one level to a row, by the least total distance. Ties go to the upper row.
 *  The planogram has as many rows as its fullest bay has levels, so no bay has more. */
export const nearestRowsInOrder = (
  levels: readonly number[],
  rowYs: readonly number[],
): number[] => {
  const n = levels.length;
  const m = rowYs.length;
  if (n === 0) return [];
  // Unreachable while rowYs come from the fullest bay; keep it so a change there fails loudly.
  if (n > m) throw new Error(`A bay has ${n} shelf levels but the planogram only ${m} rows`);
  // cost[i][j]: least distance placing levels 0..i with level i on row j.
  const cost = levels.map(() => rowYs.map(() => Number.POSITIVE_INFINITY));
  const from = levels.map(() => rowYs.map(() => -1));
  for (let j = 0; j <= m - n; j += 1) cost[0][j] = Math.abs(levels[0] - rowYs[j]);
  for (let i = 1; i < n; i += 1) {
    for (let j = i; j <= m - n + i; j += 1) {
      for (let k = i - 1; k < j; k += 1) {
        const total = cost[i - 1][k] + Math.abs(levels[i] - rowYs[j]);
        if (total < cost[i][j]) {
          cost[i][j] = total;
          from[i][j] = k;
        }
      }
    }
  }
  let row = 0;
  for (let j = 1; j < m; j += 1) if (cost[n - 1][j] < cost[n - 1][row]) row = j;
  const rows = new Array<number>(n);
  for (let i = n - 1; i >= 0; i -= 1) {
    rows[i] = row;
    row = from[i][row];
  }
  return rows;
};

export interface PogNormalizationOptions {
  placeholderSpellings?: readonly string[] | false;
  quantityAttribute?: string | false;
  nameBasedUnitInference?: boolean;
  liquidNameTerms?: readonly string[] | false;
  unitPreference?: readonly PogQuantityUnit[] | false;
  unitAliases?: Readonly<Record<string, PogQuantityUnit>>;
  zeroIsMissing?: boolean;
  liquidOuncesAreFluid?: boolean;
  minimumFamilyEvidence?: number;
  reconciliationRange?: { minimum: number; maximum: number };
  reconciliationBlockAttributes?: readonly string[] | false;
}

export interface PogSectionSemantics {
  sections: readonly string[];
  when?: Readonly<Record<string, readonly string[]>>;
  drop?: readonly string[];
  columns: Readonly<Record<string, string>>;
}

export const DEFAULT_SECTION_ATTR = "variant";

/** A column name is not an attribute; empty source writes nothing (absent ≠ blank). */
const applySectionSemantics = (
  attrs: Record<string, string | null>,
  mappingOf:
    | ((
        section: string | null,
        attrs: Readonly<Record<string, string | null>>,
      ) => ResolvedSectionMapping | undefined)
    | undefined,
  sectionAttr: string,
): ReadonlyMap<string, string> => {
  /** semantic attribute written → the live column it was read from. */
  const written = new Map<string, string>();
  const mapping = mappingOf?.(attrs[sectionAttr] ?? null, attrs);
  if (!mapping) return written;
  for (const [column, semantic] of Object.entries(mapping.columns)) {
    const value = attrs[column];
    if (value === undefined || value === null) continue;
    attrs[semantic] = value;
    written.set(semantic, column);
  }
  // Clear consumed columns after the whole mapping — a source must not be wiped mid-pass.
  for (const [column, semantic] of Object.entries(mapping.columns)) {
    if (semantic !== column && !written.has(column)) attrs[column] = null;
  }
  for (const column of mapping.drop) attrs[column] = null;
  return written;
};

interface ResolvedSectionMapping {
  drop: readonly string[];
  columns: Readonly<Record<string, string>>;
}

const sectionSemanticsResolver = (
  semantics: readonly PogSectionSemantics[] | undefined,
):
  | ((
      section: string | null,
      attrs: Readonly<Record<string, string | null>>,
    ) => ResolvedSectionMapping | undefined)
  | undefined => {
  if (!semantics || semantics.length === 0) return undefined;
  const normalized = semantics.map((mapping) => ({
    sections: new Set(mapping.sections.map((section) => section.trim().toLowerCase())),
    drop: mapping.drop ?? [],
    when: Object.entries(mapping.when ?? {}).map(
      ([column, values]) =>
        [column, new Set(values.map((value) => value.trim().toLowerCase()))] as const,
    ),
    columns: mapping.columns,
  }));
  return (section, attrs) => {
    const key = section?.trim().toLowerCase() ?? "";
    let fallback: ResolvedSectionMapping | undefined;
    for (const mapping of normalized) {
      if (mapping.sections.size === 0) {
        fallback ??= mapping;
        continue;
      }
      if (!mapping.sections.has(key)) continue;
      const matches = mapping.when.every(([column, values]) =>
        values.has(
          String(attrs[column] ?? "")
            .trim()
            .toLowerCase(),
        ),
      );
      if (matches) return mapping;
    }
    return fallback;
  };
};

export interface PogAdapterOptions {
  itemIdentifier?: "productCode" | "upc" | "itemId";
  itemIdentifierByProductCode?: Readonly<Record<string, string>>;
  attributeOverlay?: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  overlayCandidates?: readonly AttributeCandidate[];
  /** Truncated POG names split unit families the data does not have. */
  nameOverlay?: Readonly<Record<string, string | null>>;
  /** The item-master attribute holding the brand, "brand" when not given. The planogram's own
   *  brand column is always the brand. */
  brandAttribute?: string;
  normalization?: PogNormalizationOptions | false;
  sectionSemantics?: readonly PogSectionSemantics[];
  sectionAttr?: string;
}

type ProductIdentity = Pick<BaseProduct, "upc" | "id" | "productCode">;

export const itemMasterIdentifierOf = (
  item: ProductIdentity,
  strategy: NonNullable<PogAdapterOptions["itemIdentifier"]> = "productCode",
): string => {
  if (strategy === "upc") return item.upc;
  if (strategy === "itemId") return item.id;
  return item.productCode;
};

export interface PogProductReference {
  productCode: string;
  itemId: string;
  upc: string;
  name: string;
  sourceValues: Readonly<Record<string, string | null>>;
  conflictingSourceFields?: readonly string[];
}

const sourceValue = (value: unknown): string | null => {
  const text = String(value ?? "").trim();
  return text || null;
};

export const pogProductIdentifierOf = (
  product: PogProductReference,
  strategy: NonNullable<PogAdapterOptions["itemIdentifier"]> = "productCode",
): string => {
  if (strategy === "upc") return product.upc;
  if (strategy === "itemId") return product.itemId;
  return product.productCode;
};

export const pogProductReferencesOf = (pogs: readonly Pog[]): PogProductReference[] => {
  const products = new Map<string, PogProductReference>();
  const mergeValue = (left: string | null, right: string | null): string | null => {
    if (!left) return right;
    if (!right) return left;
    return left === right ? left : null;
  };
  pogs.forEach((pog) =>
    pog.planogram.bays.forEach((bay) =>
      productsOfBay(bay).forEach((item) => {
        const productCode = item.productCode || item.id || item.upc;
        const next: PogProductReference = {
          productCode,
          itemId: item.id,
          upc: item.upc,
          name: item.name,
          sourceValues: {
            brand: sourceValue(item.brand),
            variant: sourceValue(item.variant),
            categoryCode: sourceValue(item.categoryCode),
            subCategory: sourceValue(item.subCategory),
            size: sourceValue(item.size),
            color: sourceValue(item.color),
            cdt1: sourceValue(item.cdt1),
            cdt2: sourceValue(item.cdt2),
            cdt3: sourceValue(item.cdt3),
          },
        };
        const existing = products.get(productCode);
        if (!existing) {
          products.set(productCode, next);
          return;
        }
        const conflictingSourceFields = new Set(existing.conflictingSourceFields ?? []);
        const sourceValues = Object.fromEntries(
          Object.keys(next.sourceValues).map((key) => {
            if (conflictingSourceFields.has(key)) return [key, null];
            const left = existing.sourceValues[key] ?? null;
            const right = next.sourceValues[key] ?? null;
            if (left && right && left !== right) {
              conflictingSourceFields.add(key);
              return [key, null];
            }
            return [key, mergeValue(left, right)];
          }),
        );
        products.set(productCode, {
          ...existing,
          itemId: mergeValue(existing.itemId || null, next.itemId || null) ?? "",
          upc: mergeValue(existing.upc || null, next.upc || null) ?? "",
          sourceValues,
          conflictingSourceFields: [...conflictingSourceFields],
        });
      }),
    ),
  );
  return [...products.values()];
};

interface ResolvedNormalizationOptions {
  placeholderSpellings: readonly string[] | false;
  quantityAttribute: string | false;
  nameBasedUnitInference: boolean;
  liquidNameTerms: readonly string[] | false;
  unitPreference: readonly PogQuantityUnit[] | false;
  unitAliases: Readonly<Record<string, PogQuantityUnit>>;
  zeroIsMissing: boolean;
  liquidOuncesAreFluid: boolean;
  minimumFamilyEvidence: number;
  reconciliationRange: { minimum: number; maximum: number };
  reconciliationBlockAttributes: readonly string[] | false;
}

/* Both namespaces — a missing attr is ""; dropping either widens the block. */
const DEFAULT_RECONCILIATION_BLOCK_ATTRIBUTES: readonly string[] = [
  "variant",
  "subCategory",
  "brand",
  "form",
  "packType",
  "cdt1",
  "cdt2",
  "cdt3",
];

const resolveNormalization = (
  normalization: PogAdapterOptions["normalization"],
): ResolvedNormalizationOptions => {
  if (normalization === false) {
    return {
      placeholderSpellings: false,
      quantityAttribute: false,
      nameBasedUnitInference: false,
      liquidNameTerms: false,
      unitPreference: false,
      unitAliases: {},
      zeroIsMissing: false,
      liquidOuncesAreFluid: false,
      minimumFamilyEvidence: 2,
      reconciliationRange: { minimum: 0.8, maximum: 2 },
      reconciliationBlockAttributes: false,
    };
  }
  return {
    placeholderSpellings: normalization?.placeholderSpellings ?? DEFAULT_PLACEHOLDER_SPELLINGS,
    quantityAttribute: normalization?.quantityAttribute ?? "size",
    nameBasedUnitInference: normalization?.nameBasedUnitInference ?? true,
    liquidNameTerms: normalization?.liquidNameTerms ?? DEFAULT_LIQUID_NAME_TERMS,
    unitPreference: normalization?.unitPreference ?? DEFAULT_NAME_UNIT_PREFERENCE,
    unitAliases: normalization?.unitAliases ?? DEFAULT_UNIT_ALIASES,
    zeroIsMissing: normalization?.zeroIsMissing ?? true,
    liquidOuncesAreFluid: normalization?.liquidOuncesAreFluid ?? true,
    minimumFamilyEvidence: normalization?.minimumFamilyEvidence ?? 2,
    reconciliationRange: normalization?.reconciliationRange ?? { minimum: 0.8, maximum: 2 },
    reconciliationBlockAttributes:
      normalization?.reconciliationBlockAttributes ?? DEFAULT_RECONCILIATION_BLOCK_ATTRIBUTES,
  };
};

export const pogToStudioItems = (
  pogs: readonly Pog[],
  options: PogAdapterOptions = {},
): StudioPogData => {
  const items = new Map<string, StudioItem>();
  const runs: StudioRunGeometry[] = [];
  const shelfRestrictions: PogShelfRestriction[] = [];
  const normalization = resolveNormalization(options.normalization);
  const mappingOf = sectionSemanticsResolver(options.sectionSemantics);
  const sectionAttr = options.sectionAttr ?? DEFAULT_SECTION_ATTR;

  const attrsOf = (product: BaseProduct, itemMasterId: string) => {
    const overlayRaw = options.attributeOverlay?.[itemMasterId];
    const nameWords = nameWordsOf(options.nameOverlay?.[itemMasterId] ?? product.name);
    const overlay = overlayRaw
      ? Object.fromEntries(
          Object.entries(overlayRaw).map(([attr, value]) => [
            attr,
            attrValue(
              value,
              normalization.placeholderSpellings,
              attr === (options.brandAttribute ?? POG_BRAND_ATTR) ? nameWords : undefined,
            ),
          ]),
        )
      : undefined;
    const pogAttrs: Record<string, string | null> = {
      subCategory: attrValue(product.subCategory, normalization.placeholderSpellings),
      brand: attrValue(product.brand, normalization.placeholderSpellings, nameWords),
      cdt1: attrValue(product.cdt1, normalization.placeholderSpellings),
      cdt2: attrValue(product.cdt2, normalization.placeholderSpellings),
      cdt3: attrValue(product.cdt3, normalization.placeholderSpellings),
      variant: attrValue(product.variant, normalization.placeholderSpellings),
      size: attrValue(product.size, normalization.placeholderSpellings),
    };
    const quantityAttribute = normalization.quantityAttribute;
    const enrichQuantityOf = (source: Record<string, string | null>) => {
      if (!quantityAttribute || !normalization.nameBasedUnitInference) return;
      source[quantityAttribute] = enrichQuantityUnit(
        source[quantityAttribute] ?? null,
        options.nameOverlay?.[itemMasterId] ?? product.name,
        normalization.liquidNameTerms,
        normalization.unitPreference,
        normalization.unitAliases,
        normalization.zeroIsMissing,
        normalization.liquidOuncesAreFluid,
      );
    };
    const quantityIsPogAuthored = !!quantityAttribute && POG_AUTHORED_ATTRS.has(quantityAttribute);
    if (quantityIsPogAuthored) enrichQuantityOf(pogAttrs);
    // `variant` is the store's CDT0, which the backend matches a release against: an item-master
    // column that happens to share the name may fill a missing or placeholder one, never a real one.
    const pogCdt0 = pogAttrs.variant === UNSPECIFIED ? null : pogAttrs.variant;
    const attrs: Record<string, string | null> = {
      ...pogAttrs,
      ...overlay,
      ...(pogCdt0 !== null ? { variant: pogCdt0 } : {}),
    };
    if (!quantityIsPogAuthored) enrichQuantityOf(attrs);
    // Semantics last so overlay wins first. Re-enrich only digits — a band label is not missing size.
    const semanticAttrs = applySectionSemantics(attrs, mappingOf, sectionAttr);
    if (
      quantityAttribute &&
      semanticAttrs.has(quantityAttribute) &&
      /\d/.test(String(attrs[quantityAttribute] ?? ""))
    ) {
      enrichQuantityOf(attrs);
    }
    /* Provenance: which keys the item master supplied — its own
       columns in their own order, plus any semantic attribute a
       section mapping filled FROM one of those columns. */
    const overlayKeys = overlayRaw
      ? Object.keys(overlayRaw).filter((attr) => attr !== "variant" || pogCdt0 === null)
      : null;
    const overlayAttrs = overlayKeys
      ? [
          ...overlayKeys,
          ...[...semanticAttrs]
            .filter(
              ([semantic, source]) =>
                overlayKeys.includes(source) && !overlayKeys.includes(semantic),
            )
            .map(([semantic]) => semantic),
        ]
      : null;
    return { attrs, overlayAttrs };
  };

  const addPlacement = (
    runIndex: number,
    product: BaseProduct,
    placement: StudioPlacement,
    measure: () => Pick<
      StudioItem,
      "facingWidthCm" | "heightCm" | "dosDays" | "dosPerFacing" | "weightKg"
    >,
  ) => {
    const skuId = product.productCode || product.id;
    const existing = items.get(skuId);
    if (existing) {
      items.set(skuId, {
        ...existing,
        facings: existing.facings + placement.hFacings,
        placements: [...existing.placements, placement],
      });
      return;
    }
    const itemMasterId =
      options.itemIdentifierByProductCode?.[skuId] ??
      itemMasterIdentifierOf(product, options.itemIdentifier);
    const { attrs, overlayAttrs } = attrsOf(product, itemMasterId);
    const { facingWidthCm, heightCm, dosDays, dosPerFacing, weightKg } = measure();
    items.set(skuId, {
      skuId,
      itemMasterId,
      upc: product.upc,
      name: options.nameOverlay?.[itemMasterId] ?? product.name,
      run: runIndex,
      facingWidthCm,
      heightCm,
      facings: placement.hFacings,
      dosDays,
      dosPerFacing,
      maxFacings: product.maxFacings > 0 ? product.maxFacings : undefined,
      salesAmount: Number.isFinite(product.salesAmount) ? product.salesAmount : undefined,
      // Undefined when unstated so heavy-low rules stay inert.
      weightKg,
      attrs,
      priorities: prioritiesOf(product),
      ...(overlayAttrs ? { overlayAttrs } : {}),
      placements: [placement],
    });
  };

  pogs.forEach((pog, runIndex) => {
    const bays = pog.planogram.bays;
    const rawBayWidth = bays[0]?.width ?? 0;
    /* Rows the WALL has, not the rows the first bay happens to have: reading the shelf list off
       bay 1 loses every board the taller bays carry, and fullness then never visits them. */
    const { rankOf, rackOf, rows, hangLines, hangRankOf } = shelfRowsOf(bays);
    runs.push({
      run: runIndex,
      name: `Aisle ${runIndex + 1}`,
      widthCm: bays.reduce((sum, bay) => sum + bay.width, 0),
      heightCm: pog.planogram.getMaxHeight(),
      bayWidthCm: rawBayWidth,
      bays: bays.length,
      shelves: rows.map((row) => ({
        rank: row.rank,
        yCm: row.y,
        thicknessCm: row.thicknessCm,
        spans: row.spans.map((span) => ({
          xCm: span.xCm,
          widthCm: span.widthCm,
          bays: span.bays,
        })),
        deadSpans: row.deadSpans.map((strip) => ({
          xCm: strip.xCm,
          widthCm: strip.widthCm,
        })),
        ...(row.bayBoards
          ? {
              bayBoards: row.bayBoards.map((board) => ({
                bay: board.bay,
                y: board.y,
                thickness: board.thickness,
              })),
            }
          : {}),
        ...(row.pegboard ? { pegboard: true as const } : {}),
      })),
    });
    // A pegboard can sit at its own section x, so its bay is where its holes are.
    const bayNoAt = (x: number, fallback: number): number => {
      const index = bays.findIndex((bay) => x >= bay.getXLeft() && x < bay.getXRight());
      return index >= 0 ? index + 1 : fallback;
    };

    const boardsInCell = boardsInCellOf(bays, rankOf);

    bays.forEach((bay, bayIndex) => {
      bay.shelves.forEach((shelf, shelfIndex) => {
        const shelfRank = rankOf(bayIndex, shelfIndex);
        if (shelfRank === NO_ROW) return;
        for (const restriction of shelf.variantRestrictions ?? []) {
          const value = restriction.value?.trim();
          if (!value) continue;
          shelfRestrictions.push({
            run: runIndex,
            shelf: shelfRank,
            bay: bayIndex + 1,
            type: restriction.type,
            value,
            board: shelfIndex + 1,
            boards: boardsInCell(bayIndex, shelfIndex),
          });
        }
        for (const item of shelf.items) {
          if (!(item instanceof PlanogramItem)) continue;
          // The slot, not the bare product: shelf dividers are occupied space, not air.
          const layout = item.getLayout();
          let xLeft = layout.slotXLeft;
          let shrink = 1;
          const rack = rackOf(bayIndex, shelfIndex);
          const onRack =
            rack && inSpan(rack, { xLeft: layout.slotXLeft, xRight: layout.slotXRight })
              ? rack
              : undefined;
          if (onRack) {
            shrink = 1 / onRack.levels;
            xLeft =
              onRack.from + (onRack.level * (onRack.to - onRack.from) + xLeft - onRack.from) * shrink;
          }
          addPlacement(
            runIndex,
            item,
            {
              run: runIndex,
              shelf: shelfRank,
              // The authored bay: `getCoordinates()` lays a merged run out as one
              // lane, so an x alone can put this item in the neighbouring bay.
              bay: bayIndex + 1,
              xCm: xLeft,
              widthCm: (layout.slotXRight - layout.slotXLeft) * shrink,
              hFacings: item.getHorizontalFacings(),
              ...(boardsInCell(bayIndex, shelfIndex) > 1 && { board: shelfIndex + 1 }),
              ...(onRack && {
                rackBoard: {
                  level: onRack.level,
                  levels: onRack.levels,
                  xCm: layout.slotXLeft,
                  widthCm: layout.slotXRight - layout.slotXLeft,
                  fromCm: onRack.from,
                  toCm: onRack.to,
                  rowYCm: onRack.rowY,
                },
              }),
              ...(shelf.type === "HANGCELL" ? {} : { stackHeightCm: item.getBlockHeight() }),
            },
            () => {
              let dosDays: number | undefined;
              let dosPerFacing: number | undefined;
              try {
                dosDays = item.getDaysOfSupply();
                if (Number.isFinite(dosDays) && item.getHorizontalFacings() > 0) {
                  dosPerFacing = dosDays / item.getHorizontalFacings();
                } else {
                  dosDays = undefined;
                }
              } catch {
                dosDays = undefined;
              }
              return {
                facingWidthCm: item.getWidth() * shrink,
                heightCm: item.getHeight(),
                dosDays,
                dosPerFacing,
                weightKg: statedWeightKg(item),
              };
            },
          );
        }
      });

      for (const line of hangLines) {
        if (line.bayIndex !== bayIndex) continue;
        for (const hanging of line.placements) {
          const product = hanging.pegProduct;
          const xs = holeXsOf(hanging);
          const xLeft = Math.min(...xs);
          const xRight = Math.max(...xs) + product.unitWidth;
          addPlacement(
            runIndex,
            product,
            {
              run: runIndex,
              shelf: hangRankOf(line.y),
              bay: bayNoAt(xLeft, bayIndex + 1),
              xCm: xLeft,
              widthCm: xRight - xLeft,
              // One hole holds one facing; holes stacked in a column are vertical facings.
              hFacings: new Set(hanging.holes.map((hole) => hole.holeNoLeft)).size,
              pegboard: true,
            },
            () => ({
              facingWidthCm: product.unitWidth,
              heightCm: product.unitHeight,
              dosDays: undefined,
              dosPerFacing: undefined,
              weightKg: statedWeightKg(product),
            }),
          );
        }
      }
    });
  });

  // All-placeholder columns carry no level.
  const list = [...items.values()];
  if (normalization.quantityAttribute && normalization.reconciliationBlockAttributes !== false) {
    reconcileQuantityLabels(
      list,
      normalization.quantityAttribute,
      normalization.reconciliationBlockAttributes,
      normalization.minimumFamilyEvidence,
      normalization.reconciliationRange,
      normalization.liquidOuncesAreFluid,
    );
  }
  const pool = options.overlayCandidates ?? POG_ATTRIBUTE_CANDIDATES;
  const candidates = pool.filter((candidate) =>
    list.some((item) => !isPlaceholderValue(item.attrs[candidate.attr])),
  );

  return { items: list, runs, candidates: [...candidates], shelfRestrictions };
};

/**
 * Where a shelf a generation REQUEST names sits on the served plan: its physical row rank
 * (1 = top, the rank a placement carries) and its 1-based bay.
 *
 * The request names a shelf by `bay_no` and `shelf_no` (1 = the top shelf of that bay) and by
 * `fixtureId`. The fixture id is tried first, against the shelf's `uniqueId`, because it names
 * one board whatever the bay's shelf count; otherwise the `shelf_no`-th board from the top of
 * bay `bay_no`. A shelf the plan does not have resolves to null.
 */
export const pogShelfRowResolver = (
  pog: Pog,
): ((
  bayNo: number,
  shelfNo: number,
  fixtureId?: string,
) => { bay: number; shelf: number; board?: number } | null) => {
  const bays = pog.planogram.bays;
  const { rankOf } = shelfRowsOf(bays);
  const boardsInCell = boardsInCellOf(bays, rankOf);
  const cellOf = (bayIndex: number, shelfIndex: number, rank: number) => ({
    bay: bayIndex + 1,
    shelf: rank,
    ...(boardsInCell(bayIndex, shelfIndex) > 1 && { board: shelfIndex + 1 }),
  });
  const byFixtureId = new Map<string, { bay: number; shelf: number; board?: number }>();
  bays.forEach((bay, bayIndex) =>
    bay.shelves.forEach((shelf, shelfIndex) => {
      const rank = rankOf(bayIndex, shelfIndex);
      if (shelf.uniqueId && rank !== NO_ROW)
        byFixtureId.set(shelf.uniqueId, cellOf(bayIndex, shelfIndex, rank));
    }),
  );
  const topDown = bays.map((bay) =>
    bay.shelves
      .map((shelf, shelfIndex) => ({ shelfIndex, y: shelf.getYBottom() }))
      .sort((a, b) => b.y - a.y || a.shelfIndex - b.shelfIndex)
      .map((board) => board.shelfIndex),
  );
  return (bayNo, shelfNo, fixtureId) => {
    const byId = fixtureId ? byFixtureId.get(fixtureId) : undefined;
    if (byId) return byId;
    const bayIndex = bayNo - 1;
    const shelfIndex = topDown[bayIndex]?.[shelfNo - 1];
    const rank = shelfIndex === undefined ? NO_ROW : rankOf(bayIndex, shelfIndex);
    return rank === NO_ROW ? null : cellOf(bayIndex, shelfIndex!, rank);
  };
};
